import { LINE_STRING, POINT } from 'client/utils/constants/geometryType';
import {
    isPointCoordinate,
    areSamePoint,
    readGeometryCoordinates,
    getExtentExceeded,
    getDefaultStyleVariant
} from '../validationUtils';
import { DRAW_STAGES } from '../drawStageConstants';

const normalizeLineCoordinates = (geometry) => {
    const coordinates = readGeometryCoordinates(geometry);
    if (!coordinates) {
        return [];
    }

    return coordinates.filter((point) => isPointCoordinate(point));
};

const hasAtLeastTwoDistinctPoints = (geometry) => {
    const points = normalizeLineCoordinates(geometry);
    if (points.length < 2) {
        return false;
    }

    const distinct = new Set(points.map(([x, y]) => `${x}:${y}`));
    return distinct.size >= 2;
};

const isSinglePointStage = (geometryType, geometry) => {
    if (geometryType === POINT) {
        return true;
    }

    if (geometryType !== LINE_STRING) {
        return false;
    }

    const points = normalizeLineCoordinates(geometry);
    if (points.length === 1) {
        return true;
    }

    if (points.length >= 2) {
        return points.every((point) => areSamePoint(point, points[0]));
    }

    return false;
};

const getPlottedPointCount = (geometry) => {
    const coordinates = readGeometryCoordinates(geometry);
    if (!coordinates || coordinates.length === 0) {
        return 0;
    }

    if (coordinates.length === 1) {
        return 1;
    }

    // During active drawing OL appends cursor position as the last coordinate;
    // guidance should count only user-plotted points.
    const placedPoints = coordinates.slice(0, -1).filter((point) => isPointCoordinate(point));
    return new Set(placedPoints.map(([x, y]) => `${x}:${y}`)).size;
};

const linearSearchPolicy = Object.freeze({
    usesPolygonSketchCache: false,

    getStage ({ geometryType, geometry }) {
        if (isSinglePointStage(geometryType, geometry)) {
            return DRAW_STAGES.FIRST_POINT;
        }

        if (geometryType === LINE_STRING) {
            return hasAtLeastTwoDistinctPoints(geometry)
                ? DRAW_STAGES.LINEAR_SEARCH_ACTIVE
                : DRAW_STAGES.EDGE_SKETCH;
        }

        return DRAW_STAGES.UNKNOWN;
    },

    isComplete ({ geometryType, geometry }) {
        return geometryType === LINE_STRING && hasAtLeastTwoDistinctPoints(geometry);
    },

    shouldNotify ({ geometryType, stage, complete }) {
        if (!complete) {
            return false;
        }

        if (geometryType === LINE_STRING && stage === DRAW_STAGES.LINEAR_SEARCH_ACTIVE) {
            return true;
        }

        return false;
    },

    getExtentMessageDescriptor ({ userLimit }) {
        return {
            id: 'mapPage.error.lineTooLong',
            values: { extent: userLimit }
        };
    },

    getExtentExceeded (context) {
        return getExtentExceeded(context);
    },

    getStyleVariant (context) {
        return getDefaultStyleVariant(context);
    },

    getValidStateNotification ({ geometryType, geometry }) {
        if (geometryType !== LINE_STRING) {
            return {
                showInfo: false,
                keepInfoOpen: false,
                infoMessageDescriptor: null
            };
        }

        const plottedPoints = getPlottedPointCount(geometry);
        const shouldShowGuidance = plottedPoints <= 1;

        return {
            showInfo: shouldShowGuidance,
            keepInfoOpen: shouldShowGuidance,
            infoMessageDescriptor: shouldShowGuidance
                ? { id: 'mapToolbar.addPoints', values: undefined }
                : null
        };
    },

    getIncompleteStateNotification ({ geometryType, stage }) {
        const shouldShowGuidance = geometryType === LINE_STRING && stage === DRAW_STAGES.FIRST_POINT;
        return {
            showInfo: shouldShowGuidance,
            infoMessageDescriptor: shouldShowGuidance
                ? { id: 'mapToolbar.addPoints', values: undefined }
                : null
        };
    },

    shouldResetStatusesOnIncomplete ({ geometryType, complete }) {
        return geometryType === LINE_STRING && !complete;
    },

    shouldSkipExtentCheck ({ stage, complete }) {
        return stage === DRAW_STAGES.FIRST_POINT || !complete;
    },

    shouldValidateGeometry ({ complete }) {
        return Boolean(complete);
    }
});

export default linearSearchPolicy;

// Exported for targeted unit testing of complex helpers
export { normalizeLineCoordinates, hasAtLeastTwoDistinctPoints, isSinglePointStage, getPlottedPointCount };
